import React, { useEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { Button, Image, Modal, theme, TitleText } from '@sg/garnish';

import {
  IMAGE_HEIGHT,
  useTierUpgradeCelebrationModal,
} from './useTierUpgradeCelebrationModal';

const AnimatedDisplayText = Animated.createAnimatedComponent(TitleText);

export const TierUpgradeCelebrationModal = (
  props: TierUpgradeCelebrationModalProps,
) => {
  const {
    visible,
    headingText,
    footerText,
    btnText,
    onDismiss,
    onExplore,
    testID,
  } = props;
  const {
    startAnimation,
    stopAnimation,
    onContentLayout,
    onFooterTextLayout,
    onHeadingTextLayout,
    opacityAnimatedStyle,
    footerTextAnimatedStyle,
    headingTextAnimatedStyle,
  } = useTierUpgradeCelebrationModal();

  useEffect(() => {
    if (visible) {
      startAnimation();
    } else {
      stopAnimation();
    }
  }, [startAnimation, stopAnimation, visible]);

  return (
    <Modal
      palette="lime"
      visible={visible}
      onRequestClose={onDismiss}
      animationType="fade"
      transparent
      fitHeight
      testID={testID}
      size="full-screen"
    >
      <View style={styles.wrapper}>
        <Modal.CloseBtn
          onPress={onDismiss}
          color={theme.colors.BLACK}
          testID={`${testID}.closeBtn`}
        />
        <View style={styles.modal} onLayout={onContentLayout}>
          <AnimatedDisplayText
            style={[styles.text, headingTextAnimatedStyle]}
            onLayout={onHeadingTextLayout}
            testID={`${testID}.headingText`}
          >
            {headingText}
          </AnimatedDisplayText>
          <Animated.View style={opacityAnimatedStyle}>
            <Image
              accessibilityIgnoresInvertColors
              resizeMode="contain"
              style={styles.hero}
              source={{
                uri: 'https://res.cloudinary.com/sweetgreen/image/upload/v1675717917/card_lhbe3f.png',
              }}
              testID={`${testID}.image`}
            />
          </Animated.View>
          <AnimatedDisplayText
            style={[styles.text, footerTextAnimatedStyle]}
            onLayout={onFooterTextLayout}
            testID={`${testID}.footerText`}
          >
            {footerText}
          </AnimatedDisplayText>
        </View>
        <Modal.Footer style={styles.footer} withoutTopBorder>
          <Animated.View style={opacityAnimatedStyle}>
            <Button
              testID={`${testID}.btn`}
              size="large"
              rightIcon="IconArrowRight"
              palette="limeOnGreen"
              onPress={onExplore}
            >
              {btnText}
            </Button>
          </Animated.View>
        </Modal.Footer>
      </View>
    </Modal>
  );
};

// ─── STYLES ──────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    height: '100%',
  },
  text: {
    color: theme.colors.KALE,
    textAlign: 'center',
  },
  hero: {
    width: '100%',
    height: IMAGE_HEIGHT,
  },
  footer: {
    alignItems: 'center',
  },
  modal: {
    flex: 1,
    paddingHorizontal: theme.spacing.medium,
    justifyContent: 'center',
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type TierUpgradeCelebrationModalProps = {
  readonly visible: boolean;
  readonly headingText: string;
  readonly footerText: string;
  readonly btnText: string;
  readonly onDismiss: () => void;
  readonly onExplore: () => void;
} & Pick<ViewProps, 'testID'>;
